import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';

import { setOwnerBots } from '@redux/slices/appOwnerBotsSlice';
import { getOwnerBots } from '@services/owner';
import { terminateActiveRideSession } from '@services/ride';
import { safeLocalStorage } from '@utils/localstorage';
import ActivatedRewardDialog from '@components/Dialog/ActivatedRewardDialog';
import ScoreUnScoreDrive from './ScoreUnScoreDrive';
import RentalHistory from './RentalHistory';
import FrodobotsPointHistory from './FrodobotsPointHistory';
import { BOT_STATUS_TYPE } from '@utils/types';
import Spinner from '@util/spinner';
import {
	getTotalQualifiedMiles,
	receivedBotAccessInvitationData,
	getStatusRide,
	getTournamentBots
} from '@services/api';

import AddIcon from '@images/dashboard/add.svg';
import HomeIcon from '@images/dashboard/home.svg';
import NoBotOwnerIcon from '@images/dashboard/noBotOwnerIcon.png';
import OwnerBotCard from './OwnerBotCard';
import OwnerBotShareCard from './ShareCard';
import { ROUTES } from '@constants/routes';
import { usePollingInterval } from '@hooks/usePollingInterval';

// Bot lists refresh every 30s while the dashboard is visible. The hook
// usePollingInterval pauses while the tab is hidden and re-fires
// immediately on refocus, so users see fresh data without wasting
// bandwidth / battery in background tabs.
const BOTS_POLL_INTERVAL_MS = 30_000;
import { AI_CONTROLLER_PARAM } from '@constants/aiController';
import { FrodobotVisualIcon } from '@util/svgicon';
import FBTTokenIcon from '@images/dashboard/3DFBTToken.png';
import RoboCapsView from './RoboCapsView';
import { APP_MODES } from '@redux/slices/appModeSlice';

const OwnerDashboardComponent = () => {
	const router = useRouter();
	const dispatch = useDispatch();
	const userInfoFromBackend = useSelector((state) => state.appUser.backendInfo);
	const ownerBotsList = useSelector((state) => state.appOwnerBots.bots);
	const user = useSelector((state) => state?.setUserDetails?.user);
	const appMode = useSelector((state) => state?.appMode?.mode) || APP_MODES.EARTH_ROVERS;
	const [qualifiedMiles, setQualifiedMiles] = useState(0);
	const [botDeviceId, setBotDeviceId] = useState('');
	const [open, setOpen] = React.useState(false);
	const [receivedBotAccessInvitationRes, setReceivedBotAccessInvitationRes] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState(1);
	const [tournamentBots, setTournamentBots] = useState([]);
	const frodoPoints = user.frodo_points;
	const displayFrodoPoints = frodoPoints % 1 !== 0 ? frodoPoints?.toFixed(2) : frodoPoints;
	const qualifiedMilesRoundNumber = Math.round(qualifiedMiles * 100) / 100;

	const handleClose = () => {
		setOpen(false);
	};

	const fetchAndSetData = async () => {
		const response = await getOwnerBots();
		receivedBotAccessInvitationDataHandler();
		if (response?.bots?.length > 0) {
			const sortedBots = response.bots.sort((a, b) => {
				// Move bots with active_with_sam: true to the top
				if (a.active_with_sam && !b.active_with_sam) return -1;
				if (!a.active_with_sam && b.active_with_sam) return 1;

				// If active_with_sam is the same, sort by status
				if (a.status === b.status) {
					return a.name.localeCompare(b.name); // Sort alphabetically if `status` is the same
				}
				return a.status === 'personal_ready' ? -1 : 1; // Place "personal_ready" bots first
			});
			dispatch(setOwnerBots(sortedBots));
			setBotDeviceId(response?.bots[0]?.device_ref_id);
		}
		getQualifiedMileshandler();
		setIsLoading(false);
	};

	const fetchTournamentBots = async () => {
		const response = await getTournamentBots();
		if (response?.bots?.length > 0) {
			setTournamentBots(response.bots.filter((bot) => bot.active_competition));
		} else {
			setTournamentBots([]);
		}
	};

	// Both polls auto-pause while the tab is hidden — see usePollingInterval.
	usePollingInterval(fetchAndSetData, BOTS_POLL_INTERVAL_MS);
	usePollingInterval(fetchTournamentBots, BOTS_POLL_INTERVAL_MS);

	// Both handlers route by bot status. The only practical difference was
	// that the tournament path forced `aiDrive=true` for the ready/online
	// branch and `aiDrive=false` for the mission-in-progress branch, where
	// the regular handler passed `aiDrive` through to both. Keep the public
	// signatures intact (OwnerBotCard calls these as props) — just route
	// through one shared body.
	const dispatchBotByStatus = (selectedBot, readyAiDrive, inProgressAiDrive) => {
		if (
			selectedBot.status === BOT_STATUS_TYPE.PERSONAL_READY ||
			selectedBot.status === BOT_STATUS_TYPE.ONLINE
		) {
			handleReadyOnlineBot(selectedBot, readyAiDrive);
		} else if (selectedBot.status === BOT_STATUS_TYPE.MISSION_IN_PROGRESS) {
			handleInProgressMission(selectedBot, inProgressAiDrive);
		} else {
			toast.error(`The bot is currently ${selectedBot.status}.`, { position: 'top-right' });
		}
	};

	const handleBotSelection = (selectedBot, aiDrive = false) =>
		dispatchBotByStatus(selectedBot, aiDrive, aiDrive);

	const handleBotSelectionTournament = (selectedBot) =>
		dispatchBotByStatus(selectedBot, true, false);

	const handleReadyOnlineBot = (bot, tournamentDrive) => {
		const firstOwnerMission = getFirstMissionSelect(bot, bot.missions, 'open');
		console.log('firstOwnerMission', firstOwnerMission);
		if (firstOwnerMission && firstOwnerMission.id) {
			safeLocalStorage.setItem('drivingBot', {
				deviceId: bot.device_ref_id,
				missionId: firstOwnerMission.id,
				hardware_version: bot.hardware_version,
				speed_control_profile:
					bot.speed_control_profile && Object.keys(bot.speed_control_profile).length > 0
						? bot.speed_control_profile
						: ''
			});
			const route = isMobile ? ROUTES.mobilePlayControllers : ROUTES.ownerPlayControllers;
			router.push(`/${route}?tournament=${tournamentDrive}`);
		}
	};

	const handleInProgressMission = async (bot) => {
		const getRideStatus = await getStatusRide(bot?.current_ride_id);
		if (
			getRideStatus?.ride?.user_id === user?.id &&
			['active', 'visible'].includes(getRideStatus?.ride?.status) &&
			bot?.status === 'mission_in_progress'
		) {
			handleReadyOnlineBot(bot);
		} else {
			toast.error(`Currently, share user ${getRideStatus?.ride?.username} is driving your bot.`, {
				position: 'top-right'
			});
			const terminationResult = await terminateActiveRideSession(bot.current_ride_id);
			if (terminationResult && terminationResult?.ride) {
				safeLocalStorage.clearLocalStorageItems([
					'selectControllerName',
					'headlight',
					`ride_sequence_${bot?.device_ref_id}`,
					`rideId_${bot?.device_ref_id}`,
					`ride_${bot?.device_ref_id}`,
					`sessionId_${bot?.device_ref_id}`,
					`bot_status_${bot?.device_ref_id}`,
					`location_${bot?.device_ref_id}`,
					`drivingBot`,
					`keyBinding_${user?.id}`,
					`keyboradKey`,
					`WheelKey`,
					`RemoteKey`
				]);
				handleReadyOnlineBot(bot);
			} else if (terminationResult?.error) {
				toast.error(`${terminationResult.error}`, { position: 'top-right' });
			}
		}
	};

	const getFirstMissionSelect = (bot, missions, type) => {
		const findOwnerMission = missions.find((mission) => mission.type === type && mission.visible && mission.active);
		if (!findOwnerMission) {
			toast.error(`Sorry, there are no missions available for ${bot?.name} bot.`, { position: 'top-right' });
		}
		return findOwnerMission;
	};

	const getQualifiedMileshandler = async (deviceIds) => {
		const response = await getTotalQualifiedMiles();
		if (response && response.total_qualified_miles) {
			setQualifiedMiles(response.total_qualified_miles);
		}
	};

	const receivedBotAccessInvitationDataHandler = async () => {
		const receivedBotAccessInvitation = await receivedBotAccessInvitationData();
		if (receivedBotAccessInvitation && receivedBotAccessInvitation?.bot_access_invitations?.length > 0) {
			setReceivedBotAccessInvitationRes(receivedBotAccessInvitation.bot_access_invitations);
		}
	};

	const shareBotDriveHandler = async (shareBot, aiDrive = false) => {
		if (
			shareBot.access_token &&
			shareBot.bot &&
			shareBot.bot.device_ref_id &&
			shareBot.bot.status === 'personal_ready'
		) {
			await safeLocalStorage.setItem('shareBot', shareBot.bot);
			await safeLocalStorage.setItem('drivingBot', {
				deviceId: shareBot.bot.device_ref_id,
				missionId: '',
				hardware_version: shareBot.bot.hardware_version,
				speed_control_profile:
					shareBot.bot.speed_control_profile && Object.keys(shareBot.bot.speed_control_profile).length > 0
						? shareBot.bot.speed_control_profile
						: ''
			});
			const route = isMobile ? ROUTES.mobilePlayControllers : ROUTES.ownerPlayControllers;
			router.push(`/${route}?sharetoken=${shareBot.access_token}${aiDrive ? `&${AI_CONTROLLER_PARAM}=true` : ''}`);
		} else {
			toast.error(`The bot is currently ${shareBot.bot.status}.`, { position: 'top-right' });
		}
	};

	const handleTabClick = (tabNumber) => {
		setActiveTab(tabNumber);
	};

	return (
		<>
			<Spinner
				loading={isLoading}
				text="Minting..."
			/>
			<div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-between items-baseline mb-6">
				<div className="flex gap-3 items-center">
					<img
						src={HomeIcon}
						alt="home-icon"
						className="w-[18.667px] h-[21px]"
					/>
					<p className="text-black text-[20px] sm:text-2xl md:text-[32px] font-semibold leading-tight md:leading-[32px] tracking-tight md:tracking-[-1.92px]">
						Welcome back, {userInfoFromBackend?.username?.replace('#0', '')}.
					</p>
				</div>
			</div>
			{appMode === APP_MODES.ROBOCAPS ? (
				<div className="w-full flex flex-col items-start gap-[36px]">
					<RoboCapsView />
				</div>
			) : (
				<div className="w-full flex flex-col items-start gap-[36px]">
					<div className="w-full flex flex-col items-start gap-[16px]">
						<div className="w-full flex justify-end items-center align-middle">
							<Link
								href="/activate-account"
								className="flex pl-[8px] pr-[12px] py-[6px] items-center gap-[6px] rounded-[1000px] border-[1px] border-[solid] border-[#727272] bg-[#000]"
							>
								<img
									src={AddIcon}
									alt="add-icon"
								/>
								<p className="text-[#F3F3F3] text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
									Activate an Earth Rover
								</p>
							</Link>
						</div>
						{tournamentBots.length > 0 && (
							<div className="w-full flex flex-col items-start gap-[16px]">
								<div className="flex flex-col justify-center items-start gap-[4px]">
									<p className="text-[#727272] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
										Tournament Bots:
									</p>
								</div>
								<div className="w-full flex flex-wrap gap-4 sm:gap-10 justify-center sm:justify-start">
									{tournamentBots.map((bot, index) => (
										<OwnerBotCard
											key={index}
											index={index}
											bot={bot}
											handleBotSelection={handleBotSelectionTournament}
											setBotDeviceId={setBotDeviceId}
											botDeviceId={botDeviceId}
											isTournament={true}
										/>
									))}
								</div>
							</div>
						)}
						{ownerBotsList?.length > 0 ? (
							<div className="w-full flex flex-col items-start gap-[16px]">
								<div className="flex flex-col justify-center items-start gap-[4px]">
									<p className="text-[#727272] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
										My Bots:
									</p>
								</div>
								<div className="w-full flex flex-wrap gap-4 sm:gap-10 justify-center sm:justify-start">
									{ownerBotsList?.map((bot, index) => (
										<OwnerBotCard
											key={index}
											index={index}
											bot={bot}
											handleBotSelection={handleBotSelection}
											setBotDeviceId={setBotDeviceId}
											botDeviceId={botDeviceId}
										/>
									))}
								</div>
							</div>
						) : (
							<div className="flex w-full h-[211px] flex-col justify-center gap-[16px] rounded-[16px] bg-[rgba(221,_221,_221,_0.50)]">
								<div className="flex pl-0 pr-0 py-[54px] flex-col justify-center items-center gap-[24px]">
									<div className="w-[228px] h-[60px]">
										<img
											src={NoBotOwnerIcon}
											alt="no-bot-owner-icon"
											className="w-full h-full"
										/>
									</div>
									<div className="flex items-start gap-[4px]">
										<p className="text-[#868686] text-center text-[16px] not-italic font-medium leading-[20px] tracking-[-0.64px]">
											You have no Earth Rovers to drive.
										</p>
										<p
											className="text-[#2D74FF] text-center text-[16px] not-italic font-medium leading-[20px] tracking-[-0.64px] cursor-pointer"
											onClick={() => window.open('https://shop.frodobots.com/')}
										>
											Buy an Earth Rover
										</p>
									</div>
								</div>
							</div>
						)}
						{receivedBotAccessInvitationRes.length > 0 &&
							receivedBotAccessInvitationRes.some((item) => item.expired === false) && (
								<div className="w-full flex flex-col items-start gap-[16px]">
									<div className="flex flex-col justify-center items-start gap-[4px]">
										<p className="text-[#727272] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
											Shared with you:
										</p>
									</div>
									<div className="w-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-10">
										{receivedBotAccessInvitationRes?.map(
											(bot, index) =>
												!bot.expired && (
													<OwnerBotShareCard
														key={index}
														bot={bot}
														shareBotDriveHandler={shareBotDriveHandler}
													/>
												)
										)}
									</div>
								</div>
							)}
					</div>
					<ActivatedRewardDialog
						open={open}
						onClose={handleClose}
					/>
					<div className="w-full flex flex-col items-start gap-[16px]">
						<div className="w-full flex items-start gap-[16px] flex-wrap lg:flex-nowrap">
							<div className="relative w-full flex h-[124px] p-[16px] justify-between items-start rounded-[16px] bg-[#FFF]">
								<div className="w-full h-full flex flex-col justify-between items-start">
									<div className="flex flex-col items-start">
										<p className="text-[#727272] text-right text-[16px] sm:text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
											Points Earned to Date
										</p>
									</div>
									<div className="flex items-center gap-[4px]">
										<p className="text-[#000] text-[24px] not-italic font-semibold leading-[32px] tracking-[-1.92px]">
											{displayFrodoPoints}
										</p>
									</div>
								</div>
								<div className="absolute top-[-20px] right-[10px] flex justify-center items-center w-[120px] sm:w-[161px]">
									<img
										src={FBTTokenIcon}
										alt="FBT Token Icon"
										className="w-full h-auto"
									/>
								</div>
							</div>
							<div className="w-full flex h-[124px] p-[16px] justify-between items-start rounded-[16px] bg-[#FFF]">
								<div className="w-full h-full flex flex-col justify-between items-start">
									<div className="flex flex-col items-start">
										<p className="text-[#727272] text-[16px] sm:text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
											Total Qualified Miles
										</p>
									</div>
									<div className="flex items-center gap-[4px]">
										<p className="text-[#0A6AF3] text-[24px] not-italic font-semibold leading-[32px] tracking-[-1.92px]">
											{qualifiedMilesRoundNumber} miles
										</p>
									</div>
								</div>
								<div className="w-full flex flex-col justify-end items-end gap-[10px]">
									<div className="flex w-[140px] sm:w-[236px] pt-[6px] justify-center items-center -mt-[55px]">
										<FrodobotVisualIcon />
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="w-full flex flex-col items-center gap-[8px] rounded-[8px] p-[8px]">
						<div className="w-full flex bg-white rounded-[8px]">
							<div className={`flex w-[33.33%]`}>
								<button
									className="w-full flex p-[8px] items-center justify-center gap-[8px] rounded-[8px]"
									onClick={() => handleTabClick(1)}
									style={activeTab === 1 ? { backgroundColor: '#737373' } : { color: `#000000` }}
								>
									<p
										className={`${activeTab === 1 ? 'text-[#F3F3F3]' : 'text-[#000000]'} text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.32px]`}
									>
										Drive
									</p>
								</button>
							</div>
							<div className={`flex w-[33.33%]`}>
								<button
									className="w-full flex p-[8px] items-center justify-center gap-[8px] rounded-[8px]"
									onClick={() => handleTabClick(2)}
									style={activeTab === 2 ? { backgroundColor: '#737373' } : { color: '#000000' }}
								>
									<p
										className={`${activeTab === 2 ? 'text-[#F3F3F3]' : 'text-[#000000]'} text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.32px]`}
									>
										Rent to Sam
									</p>
								</button>
							</div>
							<div className="flex w-[33.33%]">
								<button
									className="w-full flex p-[8px] items-center justify-center gap-[8px] rounded-[8px]"
									onClick={() => handleTabClick(3)}
									style={activeTab === 3 ? { backgroundColor: '#737373' } : { color: '#000000' }}
								>
									<p
										className={`${activeTab === 3 ? 'text-[#F3F3F3]' : 'text-[#000000]'} text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.32px]`}
									>
										FrodoBots Points
									</p>
								</button>
							</div>
						</div>
						<div className="w-full flex flex-col items-center gap-[8px] bg-white rounded-[8px] p-[8px]">
							{activeTab === 1 && <ScoreUnScoreDrive qualifiedMiles={qualifiedMiles} />}
							{activeTab === 2 && <RentalHistory />}
							{activeTab === 3 && <FrodobotsPointHistory />}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default OwnerDashboardComponent;
